import React, { useState, useEffect, useRef } from 'react';
import ControlButtons from './ControlButtons';
import { ArrowUp } from 'react-feather';
import { Image } from 'react-bootstrap';

const Seat = ({ seatData, filter, selectSeatIds }) => {
  const filteredSeats = seatData.filter(
    (seat) =>
      seat.ticket_id === Number(filter) ||
      seat.ticket_id === 9999 ||
      seat.ticket_id === 8888,
  );

  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const isMobileDevice = false;
  const [touchStartDistance, setTouchStartDistance] = useState(null);
  // const [scaleZoom, setScaleZoom] = useState("");

  const containerRef = useRef(null);
  const seatSize = 50;
  const seatPadding = 10;
  const totalSize = seatSize + seatPadding * 2;

  const validSeats = seatData.filter(
    (seat) => seat && typeof seat.x === 'string',
  );
  let rows;
  if (validSeats.length > 0) {
    rows = Math.max(...validSeats.map((seat) => seat.x.charCodeAt(0))) - 65 + 1;
  } else {
  }

  const cols = Math.max(...seatData.map((seat) => seat.y));
  const gridWidth = cols * totalSize;
  const gridHeight = rows * totalSize;

  const [transform, setTransform] = useState({
    scale: 1,
    translateX: 0,
    translateY: 0,
  });

  const isEventInContainer = (clientX, clientY) => {
    if (!containerRef.current) return false;
    const rect = containerRef.current.getBoundingClientRect();
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    if (!isEventInContainer(e.touches[0].clientX, e.touches[0].clientY)) return;

    if (e.touches.length === 1) {
      setDragging(true);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      const distance = getDistance(e.touches[0], e.touches[1]);
      setTouchStartDistance(distance);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    setDragging(false);
    setTouchStartDistance(null);
  };

  const getDistance = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  useEffect(() => {
    const container = containerRef.current;

    const handleWheel = (e) => {
      if (!isEventInContainer(e.clientX, e.clientY)) return;

      e.preventDefault();
      e.stopPropagation();

      const zoomIntensity = 0.1;
      const scaleChange = e.deltaY < 0 ? 1 + zoomIntensity : 1 - zoomIntensity;

      setTransform((prev) => {
        const newScale = Math.max(0.5, Math.min(prev.scale * scaleChange, 10));
        const rect = container.getBoundingClientRect();

        const midX = e.clientX - rect.left;
        const midY = e.clientY - rect.top;

        const focusX = (midX - prev.translateX) / prev.scale;
        const focusY = (midY - prev.translateY) / prev.scale;

        const newTranslateX = midX - focusX * newScale;
        const newTranslateY = midY - focusY * newScale;

        return {
          scale: newScale,
          translateX: newTranslateX,
          translateY: newTranslateY,
        };
      });
    };

    const handleTouchMove = (e) => {
      e.preventDefault();

      if (e.touches.length === 1 && dragging) {
        const slowFactor = 3; // Faktor pengurangan kecepatan drag

        const deltaX = (e.touches[0].clientX - dragStart.x) / slowFactor;
        const deltaY = (e.touches[0].clientY - dragStart.y) / slowFactor;

        setTransform((prev) => ({
          ...prev,
          translateX: prev.translateX + deltaX,
          translateY: prev.translateY + deltaY,
        }));

        setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      } else if (e.touches.length === 2 && touchStartDistance) {
        const distance = getDistance(e.touches[0], e.touches[1]);
        const scaleChange = distance / touchStartDistance;

        setTransform((prev) => {
          const newScale = Math.max(
            0.5,
            Math.min(prev.scale * scaleChange, 10),
          );
          const rect = container.getBoundingClientRect();

          const midX =
            (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
          const midY =
            (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;

          const focusX = (midX - prev.translateX) / prev.scale;
          const focusY = (midY - prev.translateY) / prev.scale;

          const newTranslateX = midX - focusX * newScale;
          const newTranslateY = midY - focusY * newScale;

          return {
            scale: newScale,
            translateX: newTranslateX,
            translateY: newTranslateY,
          };
        });

        setTouchStartDistance(distance);
      }
    };

    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [transform, dragging, dragStart, touchStartDistance]);

  const handleMouseDown = (e) => {
    if (!isEventInContainer(e.clientX, e.clientY)) return;
    e.preventDefault();

    if (
      e.target.tagName === 'svg' ||
      e.target.tagName === 'rect' ||
      e.target.tagName === 'text'
    ) {
      setDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    if (!isEventInContainer(e.clientX, e.clientY)) return;
    e.preventDefault();

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    setTransform((prev) => ({
      ...prev,
      translateX: prev.translateX + deltaX,
      translateY: prev.translateY + deltaY,
    }));
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const colorSeat = (item) => {
    if (item.ticket_id === 8888) return '#00376bcc';
    if (item.status === 1 && item.seat_charged >= 100000) return '#f6d16b';
    if (item.status === 1) return 'white';
    if (item.status === 3) return 'grey';
    if (item.status === 2) return 'grey';
    if (item.status === 0) return 'grey';
    return 'white';
  };

  const getRowIndex = (row) => {
    let index = 0;
    for (let i = 0; i < row.length; i++) {
      index = index * 26 + (row.charCodeAt(i) - 65 + 1);
    }
    return index - 1; // Kurangi 1 agar indeks dimulai dari 0
  };

  const handleZoomIn = () => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.min(prev.scale + 0.1, 10), // Maksimum zoom 10x
    }));
  };

  const handleZoomOut = () => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.max(prev.scale - 0.1, 0.5), // Minimum zoom 0.5x
    }));
  };

  const handleReset = () => {
    setTransform({
      scale: 1,
      translateX: 0,
      translateY: 0,
    });
  };

  const handleMove = (direction) => {
    const moveStep = 50; // Jumlah piksel per gerakan
    setTransform((prev) => {
      switch (direction) {
        case 'up':
          return { ...prev, translateY: prev.translateY - moveStep };
        case 'down':
          return { ...prev, translateY: prev.translateY + moveStep };
        case 'left':
          return { ...prev, translateX: prev.translateX - moveStep };
        case 'right':
          return { ...prev, translateX: prev.translateX + moveStep };
        default:
          return prev;
      }
    });
  };

  return (
    <>
      <div className="d-flex align-items-center gap-3 mb-3">
        <div>
          <div className="d-flex align-items-center mt-1">
            <Image
              src={require('../../images/soul/seat-selected.png')}
              style={{ width: '20px' }}
            />
            <h6 className="my-0 mx-2 fw-lighter">Your Seat</h6>
          </div>
          <div className="d-flex align-items-center mt-1">
            <Image
              src={require('../../images/soul/seat-not-avail.png')}
              style={{ width: '20px' }}
            />
            <h6 className="my-0 mx-2 fw-lighter">Not Available</h6>
          </div>
        </div>
        <div>
          <div className="d-flex align-items-center mt-1">
            <Image
              src={require('../../images/soul/seat-paid.png')}
              style={{ width: '20px' }}
            />
            <h6 className="my-0 mx-2 fw-lighter">Best View (Paid Seat)</h6>
          </div>
          <div className="d-flex align-items-center mt-1">
            <Image
              src={require('../../images/soul/seat-avail.png')}
              style={{ width: '20px' }}
            />
            <h6 className="my-0 mx-2 fw-lighter">Not Available</h6>
          </div>
        </div>
      </div>
      <h6
        className="fw-lighter"
        style={{
          backgroundColor: '#33344B',
          width: '100%',
          textAlign: 'center',
          color: 'white',
          padding: '10px',
          margin: '0px',
          borderTopRightRadius: '5px',
          borderTopLeftRadius: '5px',
        }}
      >
        Stage
      </h6>
      <div
        style={{
          width: '100%',
          justifyItems: 'center',
          paddingBlock: '10px',
          borderRight: '1px solid gray',
          borderLeft: '1px solid gray',
        }}
      >
        <div
          ref={containerRef}
          style={{
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
            cursor: isMobileDevice ? 'default' : dragging ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          // onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${gridWidth} ${gridHeight + 100}`} // Tambahkan 100 untuk ruang ekstra di bawah
            preserveAspectRatio="xMidYMin"
            style={{
              width: '100%',
              height: `${290}px`,
              transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
              transformOrigin: '0 0',
              display: 'block',
            }}
          >
            {filteredSeats.map((seat) => (
              <g
                style={{ pointerEvents: 'all' }}
                onTouchStart={(e) => {
                  e.preventDefault(); // Mencegah event default yang bisa mengganggu
                }}
              >
                {seat.ticket_id !== 9999 && (
                  <>
                    <rect
                      x={(seat.y - 1) * totalSize + seatPadding}
                      y={getRowIndex(seat.x) * totalSize + seatPadding + 100}
                      width={seatSize}
                      height={seatSize}
                      strokeWidth={selectSeatIds.includes(seat?.id) && '20'}
                      stroke={
                        selectSeatIds.includes(seat?.id)
                          ? '#35C759'
                          : seat.ticket_id === 8888
                            ? '#00376bcc'
                            : 'black'
                      }
                      fill={colorSeat(seat)}
                      style={
                        seat.status === '1'
                          ? { cursor: 'pointer' }
                          : { cursor: 'not-allowed' }
                      }
                    />

                    <text
                      x={(seat.y - 1) * totalSize + seatPadding + seatSize / 2}
                      y={
                        getRowIndex(seat.x) * totalSize +
                        seatPadding +
                        100 +
                        seatSize / 2 +
                        5
                      }
                      textAnchor="middle"
                      // fill="black"
                      fontSize="14px"
                      fontWeight="bold"
                      style={
                        seat.status === '1'
                          ? { cursor: 'pointer' }
                          : { cursor: 'not-allowed' }
                      }
                    >
                      {seat.status !== 8888 && seat.name}
                    </text>
                  </>
                )}
              </g>
            ))}
          </svg>
        </div>
      </div>
      <ControlButtons
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleReset}
        onMove={handleMove}
      />
      <div
        className="d-flex align-items-center justify-content-center"
        style={{
          backgroundColor: '#33344B',
          width: '100%',
          textAlign: 'center',
          color: 'white',
          padding: '10px',
          borderBottomRightRadius: '5px',
          borderBottomLeftRadius: '5px',
        }}
      >
        <ArrowUp className="me-3" />
        <h6 className="m-0 fw-lighter">Seat Direction</h6>{' '}
        <ArrowUp className="ms-3" />
      </div>
    </>
  );
};

export default Seat;
